import React, { useState } from 'react'
import Checkbox from '../../Checkbox/Checkbox'
import Radio from '../../Radio/Radio'
import Button from '../../Button/Button'
import Search from '../../Search/Search'
import Icon from '../../Icon2'
import Tooltip from '../../Tooltip/Tooltip'
import { Choice, getChoiceLabel } from './selectFieldHelpers'

export interface ViewAllChoicesPopoverProps {
  choices: Choice[]
  isKeyValue: boolean
  isMultiple: boolean
  currentValue: string | string[] | null
  onApply: (newValue: string | string[] | null) => void
  onClose: () => void
  /** Field label shown as the popover header (Figma). */
  title?: string
  /** Help text; when present a `?` tooltip is shown next to the header title. */
  helpText?: string
  testId?: string
}

/**
 * The "View All Choices" popover used by RADIO/CHECKBOX when a field has more choices
 * than fit inline. Self-contained and prop-driven: it edits a local `pending` value and
 * only commits via `onApply`. Layout mirrors Figma — header, search, a toolbar that
 * toggles between browse mode (Select All / Show Only Selected) and selected-only mode
 * (Clear All / Show All), a fixed-height scrolling list, and a Cancel | Reset · Apply footer.
 */
const ViewAllChoicesPopover: React.FC<ViewAllChoicesPopoverProps> = ({
  choices,
  isKeyValue,
  isMultiple,
  currentValue,
  onApply,
  onClose,
  title,
  helpText,
  testId = 'view-all-choices-popover'
}) => {
  const [pendingMulti, setPendingMulti] = useState<string[]>(
    isMultiple && Array.isArray(currentValue) ? [...currentValue] : []
  )
  const [pendingSingle, setPendingSingle] = useState<string | null>(
    !isMultiple && typeof currentValue === 'string' ? currentValue : null
  )
  const [searchQuery, setSearchQuery] = useState('')
  const [showOnlySelected, setShowOnlySelected] = useState(false)

  const isSelected = (value: string) =>
    isMultiple ? pendingMulti.includes(value) : pendingSingle === value

  const selectedCount = isMultiple ? pendingMulti.length : pendingSingle != null ? 1 : 0
  const hasSelection = selectedCount > 0

  const query = searchQuery.trim().toLowerCase()
  const displayedChoices = choices
    .filter((c) => (showOnlySelected ? isSelected(c.value) : true))
    .filter((c) => (query ? getChoiceLabel(c, isKeyValue).toLowerCase().includes(query) : true))

  const toggle = (value: string) => {
    if (isMultiple) {
      setPendingMulti((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]))
    } else {
      setPendingSingle(value)
    }
  }

  const clearAll = () => {
    if (isMultiple) setPendingMulti([])
    else setPendingSingle(null)
  }

  const handleApply = () => {
    onApply(isMultiple ? pendingMulti : pendingSingle)
    onClose()
  }

  // Reset reverts the pending edits back to the currently-applied value (undo).
  const handleReset = () => {
    if (isMultiple) setPendingMulti(Array.isArray(currentValue) ? [...currentValue] : [])
    else setPendingSingle(typeof currentValue === 'string' ? currentValue : null)
  }

  return (
    <div className="view-all-choices-popover" data-test-id={testId}>
      {title ? (
        <div className="vac__header" data-test-id={`${testId}-header`}>
          <span className="vac__header-title">{title}</span>
          {helpText ? (
            <Tooltip content={helpText} position="top" showArrow={false} variantType="dark" type="secondary">
              <Icon icon="Question" version="v2" size="small" />
            </Tooltip>
          ) : null}
        </div>
      ) : null}

      <div className="vac__search">
        <Search
          version="v2"
          type="primary"
          searchButtonVisibility={false}
          value={searchQuery}
          placeholder="Search"
          onChange={(v: string) => setSearchQuery(v || '')}
        />
        <Icon icon="Search" version="v2" size="small" className="vac__search-icon" />
      </div>

      <div className="vac__toolbar">
        {showOnlySelected ? (
          <>
            <Button
              version="v2"
              buttonType="tertiary"
              size="small"
              onClick={clearAll}
              testId={`${testId}-clear-all`}
            >
              {`Clear All (${selectedCount})`}
            </Button>
            <Button
              version="v2"
              buttonType="tertiary"
              size="small"
              onClick={() => setShowOnlySelected(false)}
              testId={`${testId}-show-all`}
            >
              {`Show All (${choices.length})`}
            </Button>
          </>
        ) : (
          <>
            {isMultiple ? (
              <Button
                version="v2"
                buttonType="tertiary"
                size="small"
                onClick={() => setPendingMulti(choices.map((c) => c.value))}
                testId={`${testId}-select-all`}
              >
                {`Select All (${choices.length})`}
              </Button>
            ) : null}
            <Button
              version="v2"
              buttonType="tertiary"
              size="small"
              disabled={!hasSelection}
              onClick={() => setShowOnlySelected(true)}
              testId={`${testId}-show-selected`}
            >
              {`Show Only Selected (${selectedCount}/${choices.length})`}
            </Button>
          </>
        )}
      </div>

      <div className="vac__list">
        {displayedChoices.map((choice) =>
          isMultiple ? (
            <Checkbox
              key={choice.value}
              checkboxDisplayType="block"
              value={choice.value}
              checked={isSelected(choice.value)}
              onChange={() => toggle(choice.value)}
              label={getChoiceLabel(choice, isKeyValue)}
              testId={`${testId}-choice-${choice.value}`}
            />
          ) : (
            <Radio
              key={choice.value}
              radioDisplayType="block"
              value={choice.value}
              checked={isSelected(choice.value)}
              onChange={() => toggle(choice.value)}
              label={getChoiceLabel(choice, isKeyValue)}
              testId={`${testId}-choice-${choice.value}`}
            />
          )
        )}
      </div>

      <div className="vac__footer">
        <Button version="v2" buttonType="tertiary" onClick={onClose} testId={`${testId}-cancel`}>
          Cancel
        </Button>
        <div className="vac__footer-actions">
          <Button version="v2" buttonType="secondary" onClick={handleReset} testId={`${testId}-reset`}>
            Reset
          </Button>
          <Button version="v2" buttonType="primary" onClick={handleApply} testId={`${testId}-apply`}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ViewAllChoicesPopover
